import {
  Component,
  DestroyRef,
  HostListener,
  Inject,
  OnInit,
  PLATFORM_ID,
  computed,
  inject,
  signal
} from '@angular/core';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs';
import { StrapiService } from '../../services/strapi.service';
import { LanguageService } from '../../services/language.service';
import { BypassSecurityPipe } from '../../pipes/bypass-security.pipe';

/** Class op de body zodat de pagina achter de popup niet meescrollt. */
const BODY_OPEN_CLASS = 'popup-open';

const CLOSE_LABELS: { [language: string]: string } = {
  nl: 'Sluiten',
  fr: 'Fermer',
  en: 'Close'
};

@Component({
  selector: 'app-announcement-popup',
  standalone: true,
  imports: [CommonModule, BypassSecurityPipe],
  templateUrl: './announcement-popup.component.html',
  styleUrls: ['./announcement-popup.component.less']
})
export class AnnouncementPopupComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly strapiService = inject(StrapiService);
  private readonly languageService = inject(LanguageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);

  private readonly isBrowser: boolean;

  public readonly announcement = signal<any>(null);
  public readonly closeLabel = computed(
    () => CLOSE_LABELS[this.currentLanguage()] ?? CLOSE_LABELS['nl']
  );

  private readonly currentLanguage = signal<string>('nl');

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    // Enkel in de browser: in de SSR-output zou de popup al in de HTML staan
    // en zichtbaar zijn voordat de JS geladen is, dus voor hij weg te klikken is.
    if (!this.isBrowser) {
      return;
    }

    // NavigationEnd i.p.v. direct uitlezen: bij bootstrap is de initiele
    // navigatie nog niet rond en kent de LanguageService de taal nog niet.
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.languageService.getCurrentLanguage() ?? 'nl'),
        distinctUntilChanged(),
        switchMap((language) => {
          this.currentLanguage.set(language);

          return this.strapiService.getPopupAnnouncement(language);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((announcement) => {
        if (!announcement) {
          this.hide();

          return;
        }

        this.announcement.set(announcement);
        this.document.body.classList.add(BODY_OPEN_CLASS);
      });
  }

  /**
   * Sluiten verbergt de popup enkel voor deze paginaweergave. Er wordt bewust
   * niets bijgehouden: bij een volgend bezoek of een refresh mag hij opnieuw
   * verschijnen.
   */
  close(): void {
    this.hide();
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.announcement()) {
      this.close();
    }
  }

  private hide(): void {
    this.announcement.set(null);

    if (this.isBrowser) {
      this.document.body.classList.remove(BODY_OPEN_CLASS);
    }
  }
}
